import type {ReactElement, RefObject} from 'react';
import React, {useEffect, useRef} from 'react';
import {AiTextPromptBoxLoading} from '@Components/ai-text-prompt-box/components/ai-text-prompt-box-loading';
import {AiTextPromptBoxError} from '@Components/ai-text-prompt-box/components/ai-text-prompt-box-error';
import type {AiTextPromptBoxGeneratorOptionsProps} from '@Components/ai-text-prompt-box/components/ai-text-prompt-box-generator-options';
import {AiTextPromptBoxGeneratorOptions} from '@Components/ai-text-prompt-box/components/ai-text-prompt-box-generator-options';
import type {AiGeneratedContent, PromptBoxAlterationLoadingText, PromptBoxTitles} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {AiPromptBoxVariant, GENERATION_OPTIONS_COMPONENT_ID} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {EmojiPickerInput} from '@Components/emoji-picker-input';
import {hasMultiContent} from '@Libraries/ai-text-prompt-box-library';
import {Text, TextSize, TextType} from '@Components/text';
import styles from './ai-text-selected-caption-text-area.module.scss';

interface AiTextSelectedCaptionTextAreaProps extends AiTextPromptBoxGeneratorOptionsProps {
  shouldErrorBeInsideTheContentArea: undefined | boolean;
  value?: AiGeneratedContent | null;
  contentOnFocus: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  contentOnChange: (content: AiGeneratedContent) => void;
  variant: AiPromptBoxVariant;
  titles?: PromptBoxTitles;
  alterationLoadingTexts?: PromptBoxAlterationLoadingText;
  errorMessage?: string | null;
}

const TEXT_AREA_ROWS_FOR_AI_CAPTIONS = 1;
const TEXT_AREA_ROWS_FOR_AI_CAPTIONS_IN_MOBILE = 7;
const TEXT_AREA_ROWS_FOR_EMAIL_CAMPAIGN = 5;

export function AiTextSelectedCaptionTextArea({variant = AiPromptBoxVariant.MONOCONTENT, ...props}: AiTextSelectedCaptionTextAreaProps): ReactElement<any> | null {
  const primaryTextAreaRef = useRef<HTMLTextAreaElement>(null);
  const secondaryTextAreaRef = useRef<HTMLTextAreaElement>(null);
  const isError = !!props.errorMessage;

  const getVariantSpecificClasses = (): string => {
    switch (variant) {
      case AiPromptBoxVariant.MONOCONTENT:
        return `${props.isInMobileMode ? `${styles.outlined} spacing-p-2 spacing-m-b-2` : 'spacing-m-b-3 spacing-p-t-0'} `;
      case AiPromptBoxVariant.MULTICONTENT:
        return `${styles.outlined} spacing-p-3`;
      default:
        return '';
    }
  };

  const getClassesForTextArea = (): string => {
    return `
      ${styles.selectedCaptionTextBox} 
      ${props.isLoading ? '_hidden' : ''} 
      ${props.isInMobileMode ? 'body-s' : 'body-xs'}
      ${getVariantSpecificClasses()}
    `;
  };

  const updateTextInContent = (value: string): void => {
    props.contentOnChange({
      ...props.value,
      text: value,
    } as AiGeneratedContent);
  };

  useEffect(() => {
    if (!props.value?.text) {
      return;
    }

    adjustPrimaryTextAreaHeightOnContentChange();
  }, [props.value?.text]);

  const adjustPrimaryTextAreaHeightOnContentChange = (): void => {
    if (props.isInMobileMode) {
      return;
    }

    if (!primaryTextAreaRef.current || variant !== AiPromptBoxVariant.MONOCONTENT) {
      return;
    }

    const maxHeightForSelectedContentTextAreaWithError = '75px';
    const maxHeightForSelectedContentTextAreaDefault = props.isInMobileMode ? '74px' : '125px';

    primaryTextAreaRef.current.style.height = 'auto';
    primaryTextAreaRef.current.style.height = `${primaryTextAreaRef.current.scrollHeight}px`;

    if (isError) {
      primaryTextAreaRef.current.style.maxHeight = maxHeightForSelectedContentTextAreaWithError;
    } else {
      primaryTextAreaRef.current.style.maxHeight = maxHeightForSelectedContentTextAreaDefault;
    }
  };

  const updateSubtextInContent = (value: string): void => {
    props.contentOnChange({
      ...props.value,
      subtext: value,
    } as AiGeneratedContent);
  };

  const getTextAreaRowsForVariant = (): number => {
    switch (variant) {
      case AiPromptBoxVariant.MONOCONTENT:
        if (props.isInMobileMode) {
          return TEXT_AREA_ROWS_FOR_AI_CAPTIONS_IN_MOBILE;
        }
        return TEXT_AREA_ROWS_FOR_AI_CAPTIONS;
      case AiPromptBoxVariant.MULTICONTENT:
        return TEXT_AREA_ROWS_FOR_EMAIL_CAMPAIGN;
      default:
        return 1;
    }
  };

  const getEmojiPicker = (textAreaRef: RefObject<HTMLTextAreaElement | null>, callback: (val: string) => void): ReactElement<any> | null => {
    return (
      <div className={`${styles.emojiPickerContainer} ${isMultiContentMode() ? styles.multiContent : ''}`}>
        <EmojiPickerInput textAreaRef={textAreaRef} onEmojiSelectCallback={callback} />
      </div>
    );
  };

  const getOptions = (): ReactElement<any> | null => {
    if (variant === AiPromptBoxVariant.MULTICONTENT) {
      return null;
    }

    return (
      <AiTextPromptBoxGeneratorOptions
        optionsToUse={props.optionsToUse}
        id={GENERATION_OPTIONS_COMPONENT_ID}
        isDisabled={props.isLoading || isError}
        isInMobileMode={props.isInMobileMode}
        defaultValues={props.defaultValues}
        dropdownInUpdatedState={props.dropdownInUpdatedState}
        isLoading={props.isLoading}
        darkerDropdown
      />
    );
  };

  const getPrimaryLabelForVariant = (): ReactElement<any> | null => {
    switch (variant) {
      case AiPromptBoxVariant.MONOCONTENT:
        return null;
      case AiPromptBoxVariant.MULTICONTENT:
        if (!props.titles) {
          return null;
        }

        return (
          <div className="flexbox justify-content-between">
            <Text val={props.titles.primarySelectedContent} type={TextType.BODY} size={TextSize.XSMALL} bold />
          </div>
        );
      default:
        return null;
    }
  };

  const getSecondaryLabelForVariant = (): ReactElement<any> | null => {
    switch (variant) {
      case AiPromptBoxVariant.MONOCONTENT:
        return null;
      case AiPromptBoxVariant.MULTICONTENT:
        if (!props.titles) {
          return null;
        }

        return (
          <div className={props.isLoading ? 'spacing-m-t-2' : ''}>
            <Text val={props.titles.secondarySelectedContnt ?? ''} type={TextType.BODY} size={TextSize.XSMALL} bold />
          </div>
        );
      default:
        return null;
    }
  };

  const isMultiContentMode = (): boolean => {
    return variant === AiPromptBoxVariant.MULTICONTENT;
  };

  const getPrimaryLoaderClassesForVariant = (): string => {
    switch (variant) {
      case AiPromptBoxVariant.MULTICONTENT:
        return `border-s-darker spacing-p-3 spacing-m-t-1 radius-4 ${styles.alterLoadingContainer}`;
      default:
        if (props.isInMobileMode) {
          return `border-s-darker spacing-p-3 spacing-m-t-1 radius-4 spacing-m-b-3 ${styles.alterLoadingContainer}`;
        }
        return 'spacing-m-b-6';
    }
  };

  const getPrimaryTextArea = (): ReactElement<any> | null => {
    if (!props.value?.text) {
      return null;
    }

    return (
      <div className={isMultiContentMode() ? styles.positionRelative : ''}>
        {getPrimaryLabelForVariant()}
        <AiTextPromptBoxLoading
          isShown={!!props.isLoading}
          message={props.alterationLoadingTexts?.primaryAlterationLoadingMessage ?? window.i18next.t('pmwjs_updating')}
          invertedColors
          containerClasses={getPrimaryLoaderClassesForVariant()}
        />
        <textarea
          ref={primaryTextAreaRef}
          rows={getTextAreaRowsForVariant()}
          value={props.value.text}
          onFocus={props.contentOnFocus}
          onChange={(e) => {
            updateTextInContent(e.target.value);
          }}
          className={getClassesForTextArea()}
          {...(!props.isInMobileMode && {autoFocus: true})}
        />
        {getOptions()}
        {isMultiContentMode() ? getEmojiPicker(primaryTextAreaRef, updateTextInContent) : null}
      </div>
    );
  };

  const getSecondaryTextAreaIfMultiContent = (): ReactElement<any> | null => {
    if (!props.value) {
      return null;
    }

    if (!hasMultiContent(variant)) {
      return null;
    }

    return (
      <div className={styles.positionRelative}>
        {getSecondaryLabelForVariant()}
        <AiTextPromptBoxLoading
          isShown={!!props.isLoading}
          message={props.alterationLoadingTexts?.secondaryAlterationLoadingMessage ?? window.i18next.t('pmwjs_updating')}
          invertedColors
          containerClasses={`${styles.alterLoadingContainer} border-s-darker spacing-p-3 spacing-m-t-1 radius-4`}
        />
        <textarea
          ref={secondaryTextAreaRef}
          rows={getTextAreaRowsForVariant()}
          value={props.value.subtext}
          onFocus={props.contentOnFocus}
          onChange={(e) => {
            props.contentOnChange({
              ...props.value,
              subtext: e.target.value,
            } as AiGeneratedContent);
          }}
          className={getClassesForTextArea()}
        />
        {isMultiContentMode() ? getEmojiPicker(secondaryTextAreaRef, updateSubtextInContent) : null}
      </div>
    );
  };

  if (!props.value) {
    return null;
  }

  return (
    <div className={`${styles.selectedCaptionContentContainer}  ${props.isInMobileMode ? styles.mobileMode : ''}`}>
      <div className={`flex-v-row justify-content-between ${styles.aiTextsContent} ${props.isInMobileMode ? `${styles.mobileMode} body-s` : 'body-xs'}`}>
        <div className="flex-v-row">
          <AiTextPromptBoxError isError={isError} errorMessage={props.errorMessage ?? undefined} isHidden={!props.shouldErrorBeInsideTheContentArea} />
          {getPrimaryTextArea()}
          {getSecondaryTextAreaIfMultiContent()}
          {isMultiContentMode() ? null : getEmojiPicker(primaryTextAreaRef, updateTextInContent)}
        </div>
      </div>
    </div>
  );
}
